import React, { useEffect, useState } from "react";
import {
  CreateEmployee,
  UpdateEmployee,
} from "../../../../services/EmployeeMaster/Employee.service";
import { getAllRole } from "../../../../services/Role/Role.service";
import { useFormik } from "formik";
import * as Yup from "yup";
import styles from "./Employeemodal.module.css";
import { toast } from "react-toastify";

type ModalMode = "create" | "edit" | "view";

interface EmployeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  mode: ModalMode;
  employeeData?: any;
}

const EmployeeModal: React.FC<EmployeeModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  mode,
  employeeData,
}) => {
  const isView = mode === "view";
  const isEdit = mode === "edit";

  const [roles, setRoles] = useState<any[]>([]);

  // ✅ Load Roles
  const loadRole = async () => {
    try {
      const response = await getAllRole();
      const data = response?.data || [];

      if (Array.isArray(data)) {
        const mapped = data.map((c: any) => ({
          role_id: c.role_id,
          role_name: c.role_name,
        }));
        setRoles(mapped);
      } else {
        setRoles([]);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      setRoles([]);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadRole();
    }
  }, [isOpen]);

  const validationSchema = Yup.object({
    employeeCode: Yup.string().required("Employee Code is required"),
    firstName: Yup.string().required("First name is required"),
    lastName: Yup.string().required("Last name is required"),
    role_id: Yup.string().required("Role is required"),
    gender: Yup.string().required("Gender is required"),
    dateOfBirth: Yup.date().required("Date of birth is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    mobile: Yup.string()
      .matches(/^[0-9]{10}$/, "Mobile must be 10 digits")
      .required("Mobile number is required"),
    Password: isEdit
      ? Yup.string()
      : Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("Password is required"),
  });

  const formik = useFormik({
    initialValues: {
      employeeCode: "",
      firstName: "",
      lastName: "",
      role_id: "",
      role_name: "",
      gender: "",
      dateOfBirth: "",
      email: "",
      mobile: "",
      Password: "",
      status: "Active",
    },
    validationSchema: isView ? Yup.object() : validationSchema,
    onSubmit: async (values) => {
      try {
        if (isEdit) {
          const res = await UpdateEmployee(employeeData.id, values);
          toast.success(res?.data?.message || "Employee updated successfully");
        } else {
          const res = await CreateEmployee(values);
          toast.success(res?.data?.message || "Employee created successfully");
        }

        onSuccess();
        onClose();
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message || "Something went wrong"
        );
        console.error("Error saving employee:", error);
      }
    },
  });

  // ✅ Populate form
  useEffect(() => {
    if ((isEdit || isView) && employeeData) {
      formik.setValues({
        employeeCode: employeeData.employeeCode || "",
        firstName: employeeData.firstName || "",
        lastName: employeeData.lastName || "",
        role_id: employeeData.role_id || "",
        role_name: employeeData.role_name || "",
        gender: employeeData.gender || "",
        dateOfBirth: employeeData.dateOfBirth
          ? employeeData.dateOfBirth.substring(0, 10)
          : "",
        email: employeeData.email || "",
        mobile: employeeData.mobile || "",
        status: employeeData.status || "Active",
        Password: "",
      });
    } else if (mode === "create") {
      formik.resetForm();
    }
  }, [isOpen, mode, employeeData]);

  if (!isOpen) return null;

  const title =
    mode === "create"
      ? "Add Employee"
      : mode === "edit"
        ? "Edit Employee"
        : "View Employee";

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalBox}>
        <h2>{title}</h2>

        <form onSubmit={formik.handleSubmit}>
          <div className={styles.formGroup}>
            <label>Employee Code</label>
            <input
              type="text"
              name="employeeCode"
              value={formik.values.employeeCode}
              onChange={formik.handleChange}
              disabled={isView}
            />
            {!isView &&
              formik.touched.employeeCode &&
              formik.errors.employeeCode && (
                <div className={styles.error}>
                  {formik.errors.employeeCode}
                </div>
              )}
          </div>

          <div className={styles.twoColumnGrid}>
            <div className={styles.formGroup}>
              <label>First Name</label>
              <input
                type="text"
                name="firstName"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                disabled={isView}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Last Name</label>
              <input
                type="text"
                name="lastName"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                disabled={isView}
              />
            </div>
          </div>

          {/* ✅ ROLE DROPDOWN */}
          <div className={styles.formGroup}>
            <label>Role</label>
            <select
              name="role_id"
              value={formik.values.role_id}
              onChange={(e) => {
                const selected = roles.find(
                  (r) => r.role_id == e.target.value
                );
                formik.setFieldValue("role_id", e.target.value);
                formik.setFieldValue(
                  "role_name",
                  selected?.role_name || ""
                );
              }}
              disabled={isView}
            >
              <option value="">Select Role</option>
              {roles.map((role) => (
                <option key={role.role_id} value={role.role_id}>
                  {role.role_name}
                </option>
              ))}
            </select>

            {!isView &&
              formik.touched.role_id &&
              formik.errors.role_id && (
                <div className={styles.error}>
                  {formik.errors.role_id}
                </div>
              )}
          </div>

          <div className={styles.twoColumnGrid}>
            <div className={styles.formGroup}>
              <label>Gender</label>
              <select
                name="gender"
                value={formik.values.gender}
                onChange={formik.handleChange}
                disabled={isView}
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>

            <div className={styles.formGroup}>
              <label>Date of Birth</label>
              <input
                type="date"
                name="dateOfBirth"
                value={formik.values.dateOfBirth}
                onChange={formik.handleChange}
                disabled={isView}
              />
            </div>
          </div>

          <div className={styles.twoColumnGrid}>
            <div className={styles.formGroup}>
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                disabled={isView}
              />
            </div>
            <div className={styles.formGroup}>
              <label>Password</label>
              <input
                type="password"
                name="Password"
                value={formik.values.Password}
                onChange={formik.handleChange}
                disabled={isView}
              />

              {!isView &&
                formik.touched.Password &&
                formik.errors.Password && (
                  <div className={styles.error}>
                    {formik.errors.Password}
                  </div>
                )}
            </div>

            <div className={styles.formGroup}>
              <label>Mobile</label>
              <input
                type="text"
                name="mobile"
                value={formik.values.mobile}
                onChange={formik.handleChange}
                disabled={isView}
              />
            </div>
          </div>

          {(isView || isEdit) && (
            <div className={styles.formGroup}>
              <label>Status</label>
              <select
                name="status"
                value={formik.values.status}
                onChange={formik.handleChange}
                disabled={isView}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          )}

          <div className={styles.modalActions}>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={onClose}
            >
              {isView ? "Close" : "Cancel"}
            </button>

            {!isView && (
              <button type="submit" className={styles.saveBtn}>
                {isEdit ? "Update Employee" : "Save Employee"}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmployeeModal;